import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, IconButton, Table, TableHead, TableRow, TableCell, TableBody, Tooltip, Typography,
  CircularProgress, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory, useParams, useRouteMatch, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import { InvTextField, InvSelect } from '../../components/InvField';
import StockBalanceAutocomplete from '../../components/StockBalanceAutocomplete';
import {
  createGoodsDiscard,
  getGoodsDiscard,
  updateGoodsDiscard,
} from '../../api/goodsDiscard';
import { getStores } from '../../api/stockLedger';
import useInvRole from '../../hooks/useInvRole';
import { filterStoresByAssignment } from '../../utils/assignedStores';
import { toInventoryList } from '../../api/inventory-request';
import { GOODS_DISCARD_REASONS } from '../../constants/storeManagement';
import { storeDisplayName } from '../../utils/storeEntity';
import { IPD_COLORS } from '../../theme';

const LINE_DEFAULT = {
  item_key: '',
  item_id: '',
  item_name: '',
  batch_number: '',
  grn_item_id: '',
  expiry_date: '',
  uom_id: '',
  discard_quantity: '',
  reason: '',
  reason_custom: '',
  remarks: '',
  available: '',
  _batches: [],
};

function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const REASON_MAX_LENGTH = 60;

const REASON_OPTIONS = [
  { value: '', label: 'Select reason *' },
  ...GOODS_DISCARD_REASONS.map(r => ({ value: r, label: r })),
];

function parseReasonFromApi(reason) {
  const value = (reason || '').trim();
  if (!value) return { reason: '', reason_custom: '' };
  if (GOODS_DISCARD_REASONS.includes(value) && value !== 'Other') {
    return { reason: value, reason_custom: '' };
  }
  return { reason: 'Other', reason_custom: value };
}

function resolveLineReason(line) {
  if (line.reason === 'Other') return (line.reason_custom || '').trim();
  return (line.reason || '').trim();
}

export default function GoodsDiscardForm() {
  const history = useHistory();
  const { id } = useParams();
  const { url } = useRouteMatch();
  const location = useLocation();
  const isEdit = Boolean(id) && location.pathname.endsWith('/edit');
  const listUrl = url.replace(/\/(new|[^/]+\/edit)$/, '');

  const [stores, setStores] = useState([]);
  const [lines, setLines] = useState([{ ...LINE_DEFAULT }]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState(null);

  const { myStoreId, myStoreIds } = useInvRole();

  const { control, handleSubmit, watch, reset, setValue, formState: { errors } } = useForm({
    defaultValues: {
      store_id: '',
      discard_date: dayjs().format('YYYY-MM-DD'),
      remarks: '',
    },
  });

  const storeId = watch('store_id');

  useEffect(() => {
    getStores()
      .then(d => setStores(toInventoryList(d)))
      .catch(() => {});
  }, []);

  // Store-scoped users discard from their own store only. Create path only — the
  // edit path reset()s from the server and must keep the saved store.
  const lockStore = !isEdit && myStoreIds.length === 1
    && stores.some(s => (s.store_id || s.id) === myStoreId);

  useEffect(() => {
    if (lockStore) setValue('store_id', myStoreId);
  }, [lockStore, myStoreId, setValue]);

  const loadExisting = useCallback(async () => {
    if (!isEdit || !id) return;
    setLoading(true);
    setLoadError(null);
    try {
      // API: GET /inventory/goods-discard/{id}/
      const data = await getGoodsDiscard(id);
      if (data.status !== 'DRAFT') {
        setLoadError('Only DRAFT goods discards can be edited.');
        return;
      }
      reset({
        store_id: data.store_id || '',
        discard_date: data.discard_date || dayjs().format('YYYY-MM-DD'),
        remarks: data.remarks || '',
      });
      const existingLines = Array.isArray(data.lines) ? data.lines : [];
      setLines(existingLines.length ? existingLines.map(line => {
        const { reason, reason_custom } = parseReasonFromApi(line.reason);
        return {
        item_key: line.item_id || '',
        item_id: line.item_id || '',
        item_name: line.item_name || line.item_id || '',
        batch_number: line.batch_number || '',
        grn_item_id: line.grn_item_id || '',
        expiry_date: line.expiry_date || '',
        uom_id: line.uom_id || '',
        discard_quantity: line.discard_quantity ?? '',
        reason,
        reason_custom,
        remarks: line.remarks || '',
        available: line.current_quantity ?? '',
        _batches: line.batch_number ? [{
          batch_number: line.batch_number,
          grn_item_id: line.grn_item_id,
          expiry_date: line.expiry_date,
          available_quantity: line.current_quantity,
        }] : [],
      };
      }) : [{ ...LINE_DEFAULT }]);
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id, isEdit, reset]);

  useEffect(() => { loadExisting(); }, [loadExisting]);

  function updateLine(i, patch) {
    setLines(prev => prev.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  }

  function selectItem(i, key, item) {
    if (!item) {
      updateLine(i, { ...LINE_DEFAULT });
      return;
    }
    const batches = item.batches || [];
    updateLine(i, {
      item_key: key || item.item_id,
      item_id: item.item_id,
      item_name: item.item_name || item.item_code || '',
      uom_id: item.base_uom_id || '',
      batch_number: '',
      grn_item_id: '',
      expiry_date: '',
      available: item.total_available_quantity ?? '',
      _batches: batches,
    });
  }

  function selectBatch(i, batchNumber) {
    const line = lines[i];
    const batch = (line._batches || []).find(b => b.batch_number === batchNumber);
    updateLine(i, {
      batch_number: batchNumber,
      grn_item_id: batch?.grn_item_id || '',
      expiry_date: batch?.expiry_date || line.expiry_date || '',
      available: batch?.available_quantity ?? line.available,
    });
  }

  async function onSubmit(data) {
    const valid = lines.filter(l => l.item_id && toNumber(l.discard_quantity) > 0 && resolveLineReason(l));
    if (!valid.length) {
      toast.error('Add at least one line with item, quantity, and reason');
      return;
    }
    const missingCustom = valid.find(l => l.reason === 'Other' && !resolveLineReason(l));
    if (missingCustom) {
      toast.error('Enter a custom reason for lines marked as Other');
      return;
    }
    const tooLong = valid.find(l => resolveLineReason(l).length > REASON_MAX_LENGTH);
    if (tooLong) {
      toast.error(`Reason must be ${REASON_MAX_LENGTH} characters or fewer`);
      return;
    }
    if (!data.store_id) {
      toast.error('Store is required');
      return;
    }

    const overAvailable = valid.find(l => {
      const qty = toNumber(l.discard_quantity);
      const avail = toNumber(l.available);
      return avail != null && qty != null && qty > avail;
    });
    if (overAvailable) {
      toast.error(
        `Discard qty cannot exceed available (${overAvailable.available}) for ${overAvailable.item_name || 'item'}`,
      );
      return;
    }

    const body = {
      store_id: data.store_id,
      discard_date: data.discard_date,
      remarks: data.remarks || undefined,
      lines: valid.map(l => ({
        item_id: l.item_id,
        discard_quantity: Number(l.discard_quantity),
        reason: resolveLineReason(l),
        batch_number: l.batch_number || undefined,
        grn_item_id: l.grn_item_id || undefined,
        expiry_date: l.expiry_date || undefined,
        uom_id: l.uom_id || undefined,
        remarks: l.remarks || undefined,
      })),
    };

    setSaving(true);
    try {
      if (isEdit) {
        // API: PUT /inventory/goods-discard/{id}/
        await updateGoodsDiscard(id, body);
        toast.success('Goods discard updated');
        history.push(`${listUrl}/${id}`);
      } else {
        // API: POST /inventory/goods-discard/
        const created = await createGoodsDiscard(body);
        toast.success('Draft goods discard created');
        const newId = created?.id;
        history.push(newId ? `${listUrl}/${newId}` : listUrl);
      }
    } catch (e) {
      toast.error(e.message || 'Failed to save goods discard');
    } finally {
      setSaving(false);
    }
  }

  const storeOptions = filterStoresByAssignment(stores, myStoreIds).map(s => ({
    value: s.store_id || s.id,
    label: storeDisplayName(s),
  }));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: IPD_COLORS.primary }} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
        <InvButton variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => history.push(listUrl)}>
          Back
        </InvButton>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <PageHeader
        eyebrow="Inventory Management · Goods Discard"
        title={isEdit ? 'Edit Goods Discard' : 'New Goods Discard'}
        description="Creates a DRAFT — stock changes only after approve"
        actions={(
          <>
            <InvButton variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={() => history.push(listUrl)}>
              Back
            </InvButton>
            <InvButton type="submit" size="small" loading={saving}>
              {isEdit ? 'Save changes' : 'Create draft'}
            </InvButton>
          </>
        )}
      />

      <SectionCard title="Discard details">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Controller
              name="store_id"
              control={control}
              rules={{ required: 'Required' }}
              render={({ field }) => (
                <InvSelect
                  label="Store *"
                  value={field.value}
                  onChange={field.onChange}
                  options={storeOptions}
                  error={errors.store_id}
                  disabled={isEdit || lockStore}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Controller
              name="discard_date"
              control={control}
              rules={{ required: 'Required' }}
              render={({ field }) => (
                <InvTextField
                  {...field}
                  label="Discard date *"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  error={errors.discard_date}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="remarks"
              control={control}
              render={({ field }) => <InvTextField {...field} label="Remarks" multiline rows={2} />}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="Lines" sx={{ mt: 2.5 }}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 240 }}>Item *</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 140 }}>Batch</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 110 }}>Qty *</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 90 }}>Avail</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 200, minWidth: 200 }}>Reason *</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 130 }}>Expiry</TableCell>
                <TableCell sx={{ width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <StockBalanceAutocomplete
                      storeId={storeId}
                      value={line.item_key}
                      onChange={(key, item) => selectItem(i, key, item)}
                      placeholder="Search item…"
                      disabled={!storeId}
                    />
                  </TableCell>
                  <TableCell>
                    <InvSelect
                      label=""
                      value={line.batch_number}
                      onChange={e => selectBatch(i, e.target.value)}
                      options={[
                        { value: '', label: '—' },
                        ...(line._batches || []).map(b => ({
                          value: b.batch_number || '',
                          label: `${b.batch_number || '—'} · ${b.available_quantity ?? '—'}`,
                        })).filter(o => o.value),
                      ]}
                      disabled={!line.item_id}
                    />
                  </TableCell>
                  <TableCell>
                    <InvTextField
                      value={line.discard_quantity}
                      label=""
                      type="number"
                      inputProps={{
                        min: 0.01,
                        step: 0.01,
                        ...(toNumber(line.available) != null ? { max: toNumber(line.available) } : {}),
                      }}
                      error={
                        toNumber(line.discard_quantity) != null
                        && toNumber(line.available) != null
                        && toNumber(line.discard_quantity) > toNumber(line.available)
                      }
                      helperText={
                        toNumber(line.discard_quantity) != null
                        && toNumber(line.available) != null
                        && toNumber(line.discard_quantity) > toNumber(line.available)
                          ? `Max ${line.available}`
                          : undefined
                      }
                      onChange={e => updateLine(i, { discard_quantity: e.target.value })}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{line.available || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <InvSelect
                        label=""
                        value={line.reason}
                        onChange={e => {
                          const value = e.target.value;
                          updateLine(i, {
                            reason: value,
                            reason_custom: value === 'Other' ? line.reason_custom : '',
                          });
                        }}
                        options={REASON_OPTIONS}
                        error={line.item_id && !line.reason}
                      />
                      {line.reason === 'Other' && (
                        <InvTextField
                          label=""
                          value={line.reason_custom}
                          placeholder="Custom reason (max 60)"
                          inputProps={{ maxLength: REASON_MAX_LENGTH }}
                          onChange={e => updateLine(i, { reason_custom: e.target.value })}
                          error={
                            line.item_id
                            && (!line.reason_custom?.trim()
                              || line.reason_custom.length > REASON_MAX_LENGTH)
                          }
                          helperText={
                            line.reason_custom
                              ? `${line.reason_custom.length}/${REASON_MAX_LENGTH}`
                              : 'Required'
                          }
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <InvTextField
                      value={line.expiry_date || ''}
                      label=""
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      onChange={e => updateLine(i, { expiry_date: e.target.value })}
                      disabled
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      <IconButton
                        size="small"
                        aria-label="Delete row"
                        disabled={lines.length <= 1}
                        onClick={() => setLines(prev => prev.filter((_, idx) => idx !== i))}
                        sx={{ color: IPD_COLORS.error.text }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      <Tooltip title="Add row">
                        <IconButton
                          size="small"
                          aria-label="Add row below"
                          onClick={() => setLines(prev => [
                            ...prev.slice(0, i + 1),
                            { ...LINE_DEFAULT },
                            ...prev.slice(i + 1),
                          ])}
                          sx={{ color: IPD_COLORS.primary }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </SectionCard>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2.5 }}>
        <InvButton variant="outlined" onClick={() => history.push(listUrl)}>Cancel</InvButton>
        <InvButton type="submit" loading={saving}>{isEdit ? 'Save changes' : 'Create draft'}</InvButton>
      </Box>
    </Box>
  );
}
